import { html, type TemplateResult } from "lit-html";
import { store } from "../data/store";
import type { AuthIdentity, BetsResponse } from "../data/types";

interface HeaderProps {
  identityName: string | null;
  /** Wipes the saved gridstack layout and reloads. UI-PHASE-16.A. */
  onResetLayout?: () => void;
}

export function renderHeader(props: HeaderProps): TemplateResult {
  const w = store.get<AuthIdentity>("whoami");
  const authError = w.error;
  const authOk = !!w.data?.email && !w.error;

  return html`
    <header class="console-header glass">
      <h1>The Kitchen <span class="phase-stub-badge">CONSOLE</span></h1>
      <span class="console-header__subtitle">operator</span>
      <span class="spacer"></span>
      ${pnlSparkline()}
      ${props.onResetLayout ? html`
        <button class="hdr-reset-layout"
                title="Reset every cube to its default position"
                @click=${() => {
                  if (confirm("Reset every cube to the default layout?")) {
                    props.onResetLayout?.();
                  }
                }}>
          ⟲ reset layout
        </button>
      ` : ""}
      <button class="cmdk-hint"
              title="Press ⌘K to ask the console anything"
              @click=${() => {
                document.dispatchEvent(new KeyboardEvent("keydown", {
                  key: "k", metaKey: true, bubbles: true, cancelable: true,
                }));
              }}>
        <span class="cmdk-hint__icon">⌘K</span>
        <span class="cmdk-hint__label">ask the console</span>
      </button>
      ${authError ? html`
        <span class="user-pill"
              style="background:rgba(255,69,58,0.16);
                      border-color:rgba(255,69,58,0.32);
                      color:var(--accent-red-light);
                      font-family:monospace;font-size:11px;
                      max-width:520px;overflow:hidden;text-overflow:ellipsis;
                      white-space:nowrap;"
              title="${authError.message}">
          AUTH ERR · ${authError.message}
        </span>
      ` : html`
        <span class="user-pill"
              style="${authOk ? "" : "opacity:0.6;"}">
          <span class="user-pill__dot"
                style="${authOk ? "" : "background:var(--text-quaternary);"}"></span>
          ${w.data?.email ?? props.identityName ?? "loading…"}
        </span>
      `}
    </header>
  `;
}

// =============================================================
// P&L sparkline — last 7 days running cumulative
// =============================================================
function pnlSparkline(): TemplateResult {
  const e = store.get<BetsResponse>("bets");
  if (!e.data) return html``;

  const today = new Date().toISOString().slice(0, 10);
  // Build per-day pnl for the last 7 days
  const sevenDays: string[] = [];
  for (let i = 6; i >= 0; i--) {
    sevenDays.push(new Date(Date.now() - i * 86400_000).toISOString().slice(0, 10));
  }
  const dailyPnl = new Map<string, number>(sevenDays.map((d) => [d, 0]));
  let todayPnl = 0;
  for (const b of e.data.bets) {
    if (b.pnl == null || !b.date) continue;
    if (dailyPnl.has(b.date)) {
      dailyPnl.set(b.date, (dailyPnl.get(b.date) ?? 0) + b.pnl);
    }
    if (b.date === today) todayPnl += b.pnl;
  }

  const series = sevenDays.map((d) => dailyPnl.get(d) ?? 0);
  const max    = Math.max(0.01, ...series.map((v) => Math.abs(v)));
  const W = 84, H = 28, MID = H / 2;
  const points = series.map((v, i) => {
    const x = (i / (series.length - 1)) * W;
    const y = MID - (v / max) * (MID - 3);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");

  // Area under/above the zero line, color shifts with sign
  const areaPoints = `0,${MID} ${points} ${W},${MID}`;
  const cls = todayPnl > 0 ? "is-pos" : todayPnl < 0 ? "is-neg" : "is-flat";
  return html`
    <div class="hdr-pnl ${cls}"
         title="${formatTooltip(sevenDays, series)}">
      <span class="hdr-pnl__label">today</span>
      <span class="hdr-pnl__value">
        ${todayPnl >= 0 ? "+" : ""}${todayPnl.toFixed(2)}u
      </span>
      <svg class="hdr-pnl__chart" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}"
           xmlns="http://www.w3.org/2000/svg">
        <line x1="0" y1="${MID}" x2="${W}" y2="${MID}"
              stroke="rgba(255,255,255,0.10)" stroke-width="0.5"
              stroke-dasharray="2 2"/>
        <polygon points="${areaPoints}" class="hdr-pnl__area"/>
        <polyline points="${points}" fill="none" class="hdr-pnl__line"
                  stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </div>
  `;
}

function formatTooltip(dates: string[], series: number[]): string {
  return dates.map((d, i) => {
    const v = series[i] ?? 0;
    return `${d.slice(5)}: ${v >= 0 ? "+" : ""}${v.toFixed(2)}u`;
  }).join("\n");
}

